/**
 * menu-config.ts: camada PURA da configuração de menu por tenant (Parte 2).
 *
 * Sem React, sem SDK Firebase. Define o estado de domínio (quais SEGMENTOS e
 * MÓDULOS o admin desligou), o documento Firestore correspondente (com lineage)
 * e as conversões `toDoc`/`fromDoc`. O CONFIGURACOES-MENU-DESENHO §1 fixa o
 * modelo: DENY-LIST (lista de desativados), doc único por tenant em
 * `tenants/{tenantId}/config/menu`.
 *
 * Separação DOMÍNIO x DOC (espelho de `fluxo-matriz-deliberacao-doc.ts`):
 *   - o DOMÍNIO (`MenuConfig`) carrega só os toggles: `segmentosDesativados` e
 *     `modulosDesativados`. É o que a sidebar consome e a tela de Configurações
 *     edita;
 *   - o DOC (`MenuConfigDoc`) acrescenta a LINEAGE de auditoria (`tenantId`,
 *     `atualizadoPor`, `atualizadoPorUid`, `atualizadoEm`) e o `schemaVersion`.
 *     Essa lineage NÃO pertence ao domínio: a sidebar não sabe de quem é o dado
 *     nem em qual tenant. `fromDoc` a descarta.
 *
 * Fail-safe de leitura (§1.1): doc ausente ou PARCIAL cai no default (tudo
 * ligado). `fromDoc` tolera doc parcial: `segmentosDesativados`/
 * `modulosDesativados` ausentes ou não-lista viram `[]`. Nunca deixa a sidebar
 * morta por falta de config; o RBAC continua sendo a barreira real.
 *
 * DENY-LIST e não allow-list (§1.1): novos módulos/segmentos nascem ATIVOS; o
 * admin só age para ESCONDER. `MENU_CONFIG_DEFAULT` = nenhum desativado.
 *
 * `atualizadoEm` (serverTimestamp) NÃO é injetado aqui: quem carimba o timestamp
 * do servidor é a camada Firestore da Parte 4 (a única que importa o SDK). Aqui o
 * doc é puro e determinístico; `atualizadoEm` fica `null` no `toDoc`.
 */

import type { NavGroupId } from "@/features/shell/nav-model";
import type { OrbModuleId } from "@/features/auth/auth.types";

// ---------------------------------------------------------------------------
// Domínio: só os toggles (sem lineage)
// ---------------------------------------------------------------------------

/**
 * Estado de domínio da config de menu do tenant: as duas deny-lists. Ausente/[]
 * em ambas = todos os segmentos e módulos ligados (o default). A sidebar e a
 * tela de Configurações consomem este tipo; a lineage vive só no doc.
 */
export interface MenuConfig {
  /** IDs de segmentos (grupos do menu) que o admin DESLIGOU. [] = todos ligados. */
  segmentosDesativados: NavGroupId[];
  /** OrbModuleIds de itens que o admin DESLIGOU. [] = todos ligados. */
  modulosDesativados: OrbModuleId[];
}

/** Estado default: nada desativado (tudo ligado). Fail-safe da leitura ausente. */
export const MENU_CONFIG_DEFAULT: MenuConfig = {
  segmentosDesativados: [],
  modulosDesativados: [],
};

/** Versão do shape do doc, para migração futura (§1). */
export const MENU_CONFIG_SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------
// Contexto de lineage (quem/onde): vindo do tenant/usuário na camada de wiring
// ---------------------------------------------------------------------------

/**
 * Dados de lineage que a página injeta ao salvar a config. Fora do domínio:
 * a sidebar não sabe de tenant nem de autor. `atualizadoPor` é nome de exibição,
 * NUNCA email/PII (§1). O `tenantId` vai no corpo do doc por anti-planting
 * (§2.2, a rule exige `request.resource.data.tenantId == tenantId`).
 */
export interface ContextoMenuConfigDoc {
  readonly tenantId: string;
  readonly atualizadoPorUid: string;
  readonly atualizadoPor: string;
}

// ---------------------------------------------------------------------------
// MenuConfigDoc: o documento Firestore
// ---------------------------------------------------------------------------

/**
 * O documento persistido em `tenants/{tenantId}/config/menu`. Carrega as duas
 * deny-lists do domínio MAIS a lineage e o `schemaVersion`. `atualizadoEm` é
 * `unknown` porque a Parte 4 o preenche com `serverTimestamp()` na escrita; aqui
 * (`toDoc`) fica `null`.
 */
export interface MenuConfigDoc {
  // ── campos do domínio (round-trip fiel via fromDoc) ──────────────────────
  readonly segmentosDesativados: NavGroupId[];
  readonly modulosDesativados: OrbModuleId[];

  // ── lineage (descartada por fromDoc) ─────────────────────────────────────
  readonly schemaVersion: number;
  readonly tenantId: string;
  /** Nome de exibição do admin. NUNCA email/CPF. */
  readonly atualizadoPor: string;
  readonly atualizadoPorUid: string;
  /** serverTimestamp() injetado na escrita pela Parte 4; null no toDoc puro. */
  readonly atualizadoEm: unknown;
}

// ---------------------------------------------------------------------------
// toDoc: domínio + contexto -> documento Firestore
// ---------------------------------------------------------------------------

/**
 * Converte o estado de domínio `MenuConfig` mais o contexto de lineage no
 * `MenuConfigDoc` pronto para escrita. Copia os arrays (não guarda a referência
 * do chamador). `schemaVersion` fixo em `MENU_CONFIG_SCHEMA_VERSION`;
 * `atualizadoEm` fica `null` (a Parte 4 injeta o serverTimestamp).
 */
export function toDoc(
  config: MenuConfig,
  ctx: ContextoMenuConfigDoc,
): MenuConfigDoc {
  return {
    segmentosDesativados: [...config.segmentosDesativados],
    modulosDesativados: [...config.modulosDesativados],

    schemaVersion: MENU_CONFIG_SCHEMA_VERSION,
    tenantId: ctx.tenantId,
    atualizadoPor: ctx.atualizadoPor,
    atualizadoPorUid: ctx.atualizadoPorUid,
    atualizadoEm: null,
  };
}

// ---------------------------------------------------------------------------
// fromDoc: documento Firestore -> MenuConfig (descarta lineage, tolera parcial)
// ---------------------------------------------------------------------------

/**
 * Reconstrói o `MenuConfig` de domínio a partir do doc, DESCARTANDO a lineage
 * (`schemaVersion`, `tenantId`, `atualizadoPor`, `atualizadoPorUid`,
 * `atualizadoEm`). Fail-safe: tolera doc PARCIAL. Campos ausentes ou que não são
 * lista caem no default (`[]`), então um doc inexistente, truncado ou de um
 * schema antigo nunca trava a sidebar; no pior caso, tudo fica ligado (o RBAC
 * ainda restringe). O `dados` é `unknown` porque o snapshot do Firestore não
 * garante o shape.
 */
export function fromDoc(dados: unknown): MenuConfig {
  const doc = (dados ?? {}) as Partial<MenuConfigDoc>;
  return {
    segmentosDesativados: apenasLista<NavGroupId>(doc.segmentosDesativados),
    modulosDesativados: apenasLista<OrbModuleId>(doc.modulosDesativados),
  };
}

/** Devolve uma cópia do array quando `valor` é lista; senão, `[]` (fail-safe). */
function apenasLista<T>(valor: unknown): T[] {
  return Array.isArray(valor) ? (valor.slice() as T[]) : [];
}
